import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { HiOutlineMenuAlt4 } from "react-icons/hi";
import { FaSearch, FaUser, FaCaretDown, FaShoppingCart } from "react-icons/fa";
import Flex from "../../designLayouts/Flex";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { paginationItems } from "../../../constants";

const HeaderBottom = () => {
  const products = useSelector((state) => state.rkglobaltradeReducer.products);

  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);

  const navigate = useNavigate();
  const categoryRef = useRef();
  const userRef = useRef();

  // -------------------------------
  // Optimized Outside-Click Handler
  // -------------------------------
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setShowCategoryMenu(false);
      }
      if (userRef.current && !userRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // ----------------------
  // Product Search Filter
  // ----------------------
  useEffect(() => {
    const filtered = paginationItems.filter((item) =>
      item.productName.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [searchQuery]);

  return (
    <header className="w-full bg-[#F5F5F3] relative shadow-sm">
      <div className="max-w-container mx-auto">
        <Flex className="flex flex-col lg:flex-row items-start lg:items-center justify-between w-full px-4 pb-4 lg:pb-0 h-full lg:h-24">

          {/* ==============================
                CATEGORY MENU
          =============================== */}
          <div
            ref={categoryRef}
            onClick={() => setShowCategoryMenu(!showCategoryMenu)}
            className="flex h-14 cursor-pointer items-center gap-2 text-primeColor"
            aria-label="Product Categories Menu"
          >
            <HiOutlineMenuAlt4 className="w-5 h-5" />
            <p className="text-[14px] font-normal">Trade by Category</p>

            {showCategoryMenu && (
              <motion.ul
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="absolute top-36 z-50 bg-primeColor w-auto text-[#DADADA] h-auto p-4 pb-6 rounded-md"
              >
                <li className="text-gray-300 px-4 py-2 border-b border-b-gray-400 hover:text-white duration-300 cursor-pointer">
                  <Link to="/trade" aria-label="Agriculture Products">
                    Agriculture
                  </Link>
                </li>

                <li className="text-gray-300 px-4 py-2 border-b border-b-gray-400 hover:text-white duration-300 cursor-pointer">
                  <Link to="/trade" aria-label="Other Trading Categories">
                    Other
                  </Link>
                </li>
              </motion.ul>
            )}
          </div>

          {/* ==============================
                 SEARCH BAR
          =============================== */}
          <div className="relative w-full lg:w-[600px] h-[50px] bg-white flex items-center gap-2 justify-between px-6 rounded-xl border border-gray-200">
            <input
              className="flex-1 h-full outline-none placeholder:text-[#C4C4C4] placeholder:text-[14px]"
              type="text"
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
              placeholder="Search trade products, exporters & categories"
              aria-label="Search Products"
            />
            <FaSearch className="w-5 h-5" />

            {searchQuery && (
              <div className="w-full mx-auto max-h-96 bg-white top-16 absolute left-0 z-50 overflow-y-scroll shadow-2xl scrollbar-hide cursor-pointer border border-gray-200 rounded-md">
                {filteredProducts.map((item) => (
                  <div
                    key={item._id}
                    onClick={() => {
                      navigate(
                        `/product/${item.productName
                          .toLowerCase()
                          .replace(/\s+/g, "")}`,
                        { state: { item } }
                      );
                      setSearchQuery("");
                    }}
                    className="max-w-[600px] h-28 bg-gray-50 mb-3 flex items-center gap-3 hover:bg-gray-100 duration-200"
                  >
                    <img
                      className="w-24 object-contain"
                      src={item.img}
                      loading="lazy"
                      alt={`${item.productName} - Export Product Listing`}
                    />
                    <div className="flex flex-col gap-1">
                      <p className="font-semibold text-lg">{item.productName}</p>
                      <p className="text-xs text-gray-600">{item.des}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ==============================
                  USER MENU & CART
          =============================== */}
          <div className="flex gap-4 mt-2 lg:mt-0 items-center pr-6 cursor-pointer relative">
            <div ref={userRef} onClick={() => setShowUserMenu(!showUserMenu)} className="flex">
              <FaUser />
              <FaCaretDown />
            </div>

            {showUserMenu && (
              <motion.ul
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="absolute top-6 left-0 z-50 bg-primeColor w-44 text-[#DADADA] h-auto p-4 pb-6 rounded-md"
              >
                <Link to="/signin">
                  <li className="text-gray-300 px-4 py-2 border-b border-b-gray-400 hover:text-white duration-300">
                    Login
                  </li>
                </Link>
                <Link to="/signup">
                  <li className="text-gray-300 px-4 py-2 border-b border-b-gray-400 hover:text-white duration-300">
                    Sign Up
                  </li>
                </Link>
                <li className="text-gray-300 px-4 py-2 border-b border-b-gray-400 hover:text-white duration-300">
                  Profile
                </li>
                <li className="text-gray-300 px-4 py-2 hover:text-white duration-300">
                  Account Settings
                </li>
              </motion.ul>
            )}

            <Link to="/cart" aria-label="Shopping Cart">
              <div className="relative">
                <FaShoppingCart />
                <span className="absolute top-3 -right-2 text-xs w-4 h-4 flex items-center justify-center rounded-full bg-primeColor text-white font-semibold">
                  {products.length}
                </span>
              </div>
            </Link>
          </div>
        </Flex>
      </div>
    </header>
  );
};

export default HeaderBottom;
